<template>
  <v-container>
    <h1>{{ $t('settings.settings-title') }}</h1>
    <p>{{ $t('settings.settings-info') }}</p>

    <h4>{{ $t('settings.notifications-title') }}</h4>
    <MessageBox v-if="inPause" class="my-5" type="info">
      <h6>{{ $t('settings.notifications-pausetitle') }}</h6>
      <p>{{ $t('settings.notifications-pausetext') }}</p>
      <p v-html="$t('settings.notifications-pause-readmore-link', {
        link1: loGet(links, 'settings.notifications-pauselink', '#'),
      })"></p>
    </MessageBox>

    <p>{{ $t('settings.notifications-info') }}</p>

    <p class="mx-3">
      <v-row class="hover my-0 pr-1 py-1" align="center" justify="space-between">
        {{ $t('settings.activate-push') }}
        <v-switch disabled dense hide-details v-model="userdata.allowPush"></v-switch>
      </v-row>
      <v-row class="hover my-0 pr-1 py-1" align="center" justify="space-between">
        {{ $t('settings.activate-email') }}
        <v-switch dense hide-details v-model="userdata.allowEmail" :disabled="isImitatingUser()"></v-switch>
      </v-row>
      <v-row class="hover my-0 pr-1 py-1" align="center" justify="space-between">
        {{ $t('settings.activate-sms') }}
        <v-switch dense hide-details v-model="userdata.allowSMS" :disabled="isImitatingUser()"></v-switch>
      </v-row>
    </p>

    <h4>{{ $t('settings.notifications-company-title') }}</h4>
    <p>{{ $t('settings.notifications-company-p') }}</p>

    <ActionButton type="application" class="mt-3" 
      @click="showAddCompany = true" 
      actionIcon="$sl-company"
      :isDisabled="isImitatingUser()"
    >
      {{$t('labels.add-company')}}
    </ActionButton>
    <v-dialog persistent v-model="showAddCompany" max-width="400px">
      <v-card flat>
        <v-icon class="close" @click.prevent="showAddCompany = false">$sl-close</v-icon>
        <h2>{{ $t('settings.connect-company.title') }}</h2>
        <p class="font-weight-bold">
          {{ $t('settings.connect-company.by-connecting') }}
        </p>
        <p>{{ $t('settings.connect-company.start-by') }}</p>
        <p>
          {{ $t('settings.connect-company.apply-click') }}
        </p>
        <p>{{ $t('settings.connect-company.will-answer') }}</p>
        <p class="font-weight-bold" v-html="$t('settings.connect-company.contact-info')"></p>
        <p class="font-italic">
          {{ $t('settings.connect-company.please-note') }}
        </p>

        <v-select
          v-model="regionId"
          :items="
            filter(basicRegionaryEmails, function (r) {
              return r.emails.length > 0
            })
          "
          outlined
          item-text="name"
          item-value="id"
          cache-items
          hide-no-data
          label="BYN-region"
          :error-messages="regionErrors"
        />
        <AddCompany @company="companySelected" />
        <v-card-actions class="d-flex justify-end mt-6">
          <ActionButton :block="false" type="application" actionIcon="$sl-cancel" @click="showAddCompany = false">{{
            $t('labels.cancel')
          }}</ActionButton>
          <ActionButton :block="false" actionIcon="$sl-send" @click="sendEmail">{{ $t('labels.apply') }}</ActionButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ActionButton class="mt-4" 
      @click="saveUserData" 
      actionIcon="$sl-save"
      :isDisabled="isImitatingUser()"
    >
      {{ $t('labels.save') }}
    </ActionButton>
  </v-container>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton'
import AddCompany from '@/components/Common/AddCompany'
import SingleSelect from '@/components/Common/SingleSelect'
import MessageBox from '@/components/Common/MessageBox'
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import pick from 'lodash/pick'
import filter from 'lodash/filter'
import find from 'lodash/find'

// @group People
// Styled MySettings component
export default {
  name: 'MySettings',

  components: {
    ActionButton,
    AddCompany,
    SingleSelect,
    MessageBox
  },

  data: () => ({
    company: false,
    regionId: 0,
    showAddCompany: false,
    showAddCompany: false,
    links: require('../../lib/external-links.json'),
    userdata: {},
    fields: ['allowEmail', 'allowSMS', 'allowPush'],
    regionErrors: [],
    ktidPause: false
  }),

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isImitatingUser']),
    ...mapGetters('basic', ['getRegionaryEmailsById', 'basicRegionaryEmails']),

    inPause() {
      const books = this.loGet(this, 'currentUser.books', [])
      const isInPause = find(books, ['bookStatus.id', 4])

      return isInPause || this.ktidPause
    }
  },

  created() {
    this.setUserData()
    this.getRegionaryEmails()
  },

  watch: {
    currentUser: {
      handler: function () {
        this.setUserData()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('currentuser', ['updateCurrentUserData']),
    ...mapActions('basic', ['setRegionaryEmails']),

    filter: filter,

    setUserData() {
      this.userdata = pick(this.currentUser, this.fields)
      API.getQualifyingTime(this.currentUser.id)
        .then(res => {
          const ed = this.loGet(res, 'data.educations', [])
          this.ktidPause = find(ed,['qualificationTime.status','inactive'])
        })
    },

    getRegionaryEmails() {
      API.listRegionaryEmails()
        .then(({data = {}}) => {
          this.setRegionaryEmails(data)
        })
        .catch(error => {
          console.log('Error getting regionary emails:', error)
        })
    },
    /**
     * @vuese
     * Used to save user data
     * @arg The argument is an object representing user data
     */
    saveUserData() {
      API.updatePerson({data: this.userdata})
        .then(res => {
          /**
           * @vuese
           * Used to Update currentuser tc in local store  with now date
           * @arg The argument is an object representing user data
           */
          return this.updateCurrentUserData(this.userdata)
        })
        .catch(error => {
          this.$events.fire('showError', this.$t('errors.save-failure'))
          console.log('There was an error:', error, params)
        })
    },

    companySelected(val) {
      this.company = val
    },

    async sendEmail() {
      const region = this.getRegionaryEmailsById(this.regionId)
      const company = this.company[0]
      if (!company) {
        this.$events.fire('showError', {
          message: this.$t('settings.connect-company.error-organization-number'),
          timeout: 20000
        })
        return
      }
      if (!region) {
        this.regionErrors = [this.$t('settings.connect-company.validation-region')]
        this.$events.fire('showError', {
          message: this.$t('settings.connect-company.error-email'),
          timeout: 10000
        })
        return
      } else {
        this.regionErrors = []
      }

      try {
        const { data: isAdminExist } = await API.checkExistingAdmin(company.id);

        if(isAdminExist) {
          this.$events.fire('showInfo', { 
            message: this.$t('settings.admin-exist'), 
            timeout: 5000
          })
          this.showAddCompany = false;
          return;
        }

        const receivers = region.emails
          .map(e => {
            return e
          })
          .join(',')

        const mailto = 'mailto:' + receivers
        const subject = '?subject=' + 'Koppla företag'

        const bodyValue =
`Hej

Jag ansöker om rollen företagsadministratör på BYN:s Mina sidor.

Mina uppgifter:

${this.$options.filters.fullName(this.currentUser)}
${this.currentUser.personalNumber}
${this.currentUser.email}

Företaget jag önskar få rollen företagsadministratör för:

${company.name}
${company.organizationalNumber}

Om ni har frågor kan ni ringa mig på ${this.currentUser.mobilephone}

Med vänliga hälsningar
${this.$options.filters.fullName(this.currentUser)}

`
        const body = '&body=' + encodeURIComponent(bodyValue)

        const a = document.createElement('a')
        a.href = mailto + subject + body

        a.click()
      } catch (error) {
         this.$events.fire("showError", this.$t("errors.save-failure"));
      }
    }
  }
}
</script>

<style>
</style>